import { useCallback, useMemo } from 'react';
import { Workorder } from '@certhon/models';
import {
  RouteResponse,
  isRouteResult,
  isRouteError,
} from '../../dayplanning/dayplanning.types';

/**
 * Hook to resolve routes to workorders, handling via_hq complexity
 *
 * Routes array structure:
 * - routes[0]: HQ -> first workorder (leading route)
 * - routes[1..n-1]: workorder to workorder routes (with via_hq creating 2 segments)
 * - routes[n]: last workorder -> HQ (trailing route)
 *
 * When a workorder has route_option === 'via_hq', it creates TWO route segments:
 * - prev workorder -> HQ
 * - HQ -> current workorder
 *
 * This hook handles combining those segments and calculating the correct route index.
 */
export function useRouteResolution(
  workorders: Workorder[] | undefined,
  routes: RouteResponse[] | undefined,
) {
  /**
   * Get route for a specific workorder (route to this WO from previous)
   * Returns combined route info when via_hq creates 2 segments
   * Returns RouteError if any segment failed, RouteResult if all succeeded
   */
  const getRouteForWorkorder = useCallback(
    (woIndex: number): RouteResponse | null => {
      if (!routes || !workorders || woIndex === 0) return null;

      // Calculate route index by counting routes for previous workorders
      let routeIdx = 1; // Start at 1 (route[0] is HQ->WO[0])
      for (let i = 1; i < woIndex; i++) {
        if (workorders[i]?.route_option === 'via_hq') {
          routeIdx += 2; // via_hq has 2 routes
        } else {
          routeIdx += 1;
        }
      }

      const currentWo = workorders[woIndex];
      if (currentWo?.route_option === 'via_hq') {
        // Combine both route segments (prev->HQ and HQ->WO)
        const route1 = routes[routeIdx];
        const route2 = routes[routeIdx + 1];

        // Check if both routes are successful
        if (
          route1 &&
          route2 &&
          isRouteResult(route1) &&
          isRouteResult(route2)
        ) {
          return {
            ...route1,
            distance_km: (route1.distance_km || 0) + (route2.distance_km || 0),
            duration_minutes:
              (route1.duration_minutes || 0) + (route2.duration_minutes || 0),
            // Combine geometries for polyline
            geometry: [...route1.geometry, ...route2.geometry],
          };
        }

        // Return error if either segment failed
        if (isRouteError(route1) || isRouteError(route2)) {
          const errors: string[] = [];
          if (isRouteError(route1)) errors.push(route1.error);
          if (isRouteError(route2)) errors.push(route2.error);
          // Use addresses from the route segments (route1 has from, route2 has to)
          const from = (route1 as { from: string }).from;
          const to = (route2 as { to: string }).to;
          return { from, to, error: errors.join(' | ') };
        }

        return null;
      }

      // Return route as-is (could be RouteResult or RouteError)
      return routes[routeIdx] ?? null;
    },
    [routes, workorders],
  );

  // Leading route (HQ -> first workorder)
  const leadingRoute = useMemo(() => {
    if (!routes || routes.length === 0) return null;
    return routes[0] ?? null;
  }, [routes]);

  // Trailing route (last workorder -> HQ)
  const trailingRoute = useMemo(() => {
    if (!routes || routes.length === 0) return null;
    return routes[routes.length - 1] ?? null;
  }, [routes]);

  return {
    getRouteForWorkorder,
    leadingRoute,
    trailingRoute,
  };
}
